import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Save, Star, Loader2 } from 'lucide-react';
import { EmailTemplate, EmailTemplateFormData } from '@/types/emailTemplate';

interface TemplateEditorProps {
  template: EmailTemplate;
  onSave: (id: string, data: Partial<EmailTemplateFormData>) => Promise<boolean>;
  onSetDefault: (id: string) => Promise<boolean>;
  isSaving: boolean;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  template,
  onSave,
  onSetDefault,
  isSaving
}) => {
  const [formData, setFormData] = useState<EmailTemplateFormData>({
    name: template.name,
    description: template.description || '',
    intro_text: template.intro_text,
    call_to_action_text: template.call_to_action_text,
    sender_name: template.sender_name,
    sender_title: template.sender_title || '',
    sender_email: template.sender_email,
    sender_phone: template.sender_phone || '',
    editor_name: template.editor_name || '',
    email_subject: template.email_subject || '',
    is_default: template.is_default
  });

  const [hasChanges, setHasChanges] = useState(false);

  // Reset form when template changes
  useEffect(() => {
    setFormData({
      name: template.name,
      description: template.description || '',
      intro_text: template.intro_text,
      call_to_action_text: template.call_to_action_text,
      sender_name: template.sender_name,
      sender_title: template.sender_title || '',
      sender_email: template.sender_email,
      sender_phone: template.sender_phone || '',
      editor_name: template.editor_name || '',
      email_subject: template.email_subject || '',
      is_default: template.is_default
    });
    setHasChanges(false);
  }, [template]);

  const handleChange = (field: keyof EmailTemplateFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    const success = await onSave(template.id, formData);
    if (success) {
      setHasChanges(false);
    }
  };

  const handleSetDefault = async () => {
    await onSetDefault(template.id);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Malinformasjon
            {template.is_default && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                <Star className="h-3 w-3" />
                Standard
              </span>
            )}
          </CardTitle>
          <CardDescription>Grunnleggende informasjon om denne malen</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Malnavn</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="F.eks. Standard, Personlig..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Beskrivelse</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Når passer denne malen?"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>E-postinnhold</CardTitle>
          <CardDescription>Tekster som vises i e-posten</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email_subject">Tema for e-post</Label>
            <Input
              id="email_subject"
              value={formData.email_subject}
              onChange={(e) => handleChange('email_subject', e.target.value)}
              placeholder="F.eks. Briefr vil gjerne intervjue deg"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="intro_text">Introduksjonstekst</Label>
            <Textarea
              id="intro_text"
              value={formData.intro_text}
              onChange={(e) => handleChange('intro_text', e.target.value)}
              placeholder="Teksten som vises i begynnelsen av e-posten..."
              rows={5}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="call_to_action_text">Call-to-action tekst</Label>
            <Textarea
              id="call_to_action_text"
              value={formData.call_to_action_text}
              onChange={(e) => handleChange('call_to_action_text', e.target.value)}
              placeholder="Teksten som vises før 'Svar her' knappen..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Avsenderinformasjon</CardTitle>
          <CardDescription>Informasjon som vises i signaturen</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sender_name">Avsendernavn</Label>
              <Input
                id="sender_name"
                value={formData.sender_name}
                onChange={(e) => handleChange('sender_name', e.target.value)}
                placeholder="F.eks. Redaksjonen, Tom Sekse..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sender_title">Tittel (valgfritt)</Label>
              <Input
                id="sender_title"
                value={formData.sender_title}
                onChange={(e) => handleChange('sender_title', e.target.value)}
                placeholder="F.eks. Journalist"
              />
            </div>
          </div>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sender_email">E-postadresse</Label>
              <Input
                id="sender_email"
                type="email"
                value={formData.sender_email}
                onChange={(e) => handleChange('sender_email', e.target.value)}
                placeholder="epost@briefr.no"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sender_phone">Telefon (valgfritt)</Label>
              <Input
                id="sender_phone"
                value={formData.sender_phone}
                onChange={(e) => handleChange('sender_phone', e.target.value)}
                placeholder="+47 12345678"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="editor_name">Ansvarlig redaktør</Label>
            <Input
              id="editor_name"
              value={formData.editor_name}
              onChange={(e) => handleChange('editor_name', e.target.value)}
              placeholder="F.eks. Tom Sekse"
            />
          </div>
        </CardContent>
      </Card>

      <Separator />

      <div className="flex items-center justify-between">
        {!template.is_default ? (
          <Button
            variant="outline"
            onClick={handleSetDefault}
            disabled={isSaving}
          >
            <Star className="h-4 w-4 mr-2" />
            Sett som standard
          </Button>
        ) : (
          <div className="text-sm text-muted-foreground">
            Dette er standard malen som brukes som forvalg
          </div>
        )}
        
        <Button
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Lagre endringer
        </Button>
      </div>
    </div>
  );
};

export default TemplateEditor;
